import { Box, Skeleton, Theme, Typography } from "@outschool/backpack";
import { useTranslation } from "@outschool/localization";
import {
  Container,
  CurvedTopper
} from "@outschool/ui-legacy-component-library";
import React from "react";

import curatedHeroSectionBlob from "../../../images/curated-hero-section-blob.png";
import curatedHeroSectionHeart from "../../../images/curated-hero-section-heart.svg";
import curatedHeroSectionSquiggle from "../../../images/curated-hero-section-squiggle.svg";
import { DefaultValueProps } from "./DefaultValueProps";

export const MobileCategoryHero: React.FC<{
  Breadcrumbs?: React.ReactNode;
  HeroContent: React.ReactNode;
  isTutoringCLP?: boolean;
}> = ({ Breadcrumbs, HeroContent, isTutoringCLP = false }) => {
  return (
    <Box sx={{ backgroundColor: "primary.50" }}>
      {Breadcrumbs && (
        <Container sx={{ pt: "medium" }}>{Breadcrumbs}</Container>
      )}
      {HeroContent}
      {!isTutoringCLP && <CurvedTopper color="white" />}
    </Box>
  );
};

export const MobileHeroTitle: React.FC<{ title: string }> = ({ title }) => {
  return (
    <>
      <Typography
        component="h1"
        variant="h4"
        sx={(theme: Theme) => ({ py: theme.spacing(10), fontWeight: "bold" })}
      >
        {title}
      </Typography>
    </>
  );
};

export const DesktopCategoryHero: React.FC<{
  Breadcrumbs?: React.ReactNode;
  HeroTitle: React.ReactNode;
  HeroImage?: React.ReactNode;
  HeroContent: React.ReactNode;
  HeroFilters?: React.ReactNode;
  HeroDescription?: React.ReactNode;
  isTutoringCLP?: boolean;
}> = ({
  Breadcrumbs,
  HeroTitle,
  HeroImage,
  HeroContent,
  HeroFilters,
  HeroDescription,
  isTutoringCLP
}) => {
  const { t } = useTranslation(
    "client\\components\\categories\\HeroComponents"
  );
  if (!HeroDescription) {
    HeroDescription = t(
      "Find affordable, top-notch tutors who will help your kids crush their goals and gain confidence."
    );
  }
  return (
    <Box sx={{ backgroundColor: "#f0f3ff" }}>
      <Container>
        <Box
          sx={(theme: Theme) => ({
            py: theme.spacing(32)
          })}
        >
          <Box
            flex
            sx={(theme: Theme) => ({
              justifyContent: "space-between",
              [theme.breakpoints.up("md")]: {
                gap: theme.spacing(42)
              }
            })}
          >
            <Box
              sx={{
                position: "relative",
                maxWidth: isTutoringCLP ? "100%" : "53%"
              }}
            >
              {!isTutoringCLP && Breadcrumbs}
              {HeroTitle}
              {isTutoringCLP && (
                <Typography
                  variant={"h5"}
                  sx={(theme: Theme) => ({
                    color: theme.palette.grey[600],
                    borderBottom: "1px solid",
                    borderColor: theme.palette.grey[300],
                    paddingBottom: "12px",
                    marginBottom: "12px"
                  })}
                >
                  {HeroDescription}
                </Typography>
              )}
              {HeroContent}
            </Box>
            {HeroImage ? (
              <Box
                sx={(theme: Theme) => ({
                  px: theme.spacing(24),
                  paddingTop: theme.spacing(32),
                  position: "relative"
                })}
              >
                {HeroImage}
              </Box>
            ) : (
              <Box>{HeroFilters}</Box>
            )}
          </Box>
        </Box>
      </Container>
      {!isTutoringCLP && <CurvedTopper color="white" />}
    </Box>
  );
};

export const DesktopHeroTitle: React.FC<{ title: string }> = ({ title }) => {
  return (
    <>
      <Typography
        component="h1"
        variant="h2"
        sx={(theme: Theme) => ({ py: theme.spacing(24) })}
      >
        {title}
      </Typography>
    </>
  );
};

export const DesktopHeroImage: React.FC<{
  imageUrl: string;
  alt?: string;
  withSkeleton?: boolean;
}> = ({ imageUrl, withSkeleton = false, alt = "" }) => {
  const [loading, setLoading] = React.useState(true);
  const handleLoad = () => setLoading(false);
  return (
    <Box
      flex
      sx={{
        justifyContent: "end",
        alignItems: "center"
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: "420px",
          minWidth: "220px"
        }}
      >
        <img
          style={{
            position: "absolute",
            width: "125%",
            height: "140%",
            top: "-31%",
            left: "-19%"
          }}
          src={curatedHeroSectionBlob}
          alt=""
        />

        <Box
          sx={{
            position: "relative"
          }}
        >
          {withSkeleton && loading && (
            <Skeleton variant="rectangular" width="100%" />
          )}
          <img
            style={{
              position: "relative",
              borderRadius: "1em",
              padding: 0,
              margin: 0,
              width: "100%",
              visibility: loading ? "hidden" : "visible"
            }}
            onLoad={handleLoad}
            src={imageUrl}
            alt={alt}
          />
          <img
            style={{
              position: "absolute",
              left: "-15%",
              bottom: "-10%",
              height: "30%",
              width: "30%"
            }}
            src={curatedHeroSectionHeart}
            alt=""
          />
          <img
            style={{
              position: "absolute",
              right: "-7%",
              bottom: "-2%",
              height: "15%",
              width: "15%"
            }}
            src={curatedHeroSectionSquiggle}
            alt=""
          />
        </Box>
      </Box>
    </Box>
  );
};

export const HeroValueProps: React.FC<{
  contentfulValueProps: React.ReactNode;
}> = ({ contentfulValueProps }) => {
  if (!!contentfulValueProps) {
    return (
      <Box
        sx={(theme: Theme) => ({
          ul: {
            margin: 0,
            listStyleType: "none",
            li: {
              fontSize: theme.typography.body1,
              padding: theme.spacing(2)
            }
          }
        })}
      >
        {contentfulValueProps}
      </Box>
    );
  }

  return <DefaultValueProps />;
};
